"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/cn";
import { SPORTSBOOK_ROOT, isCenteredNav, isSingleRowNav } from "@/lib/site";
import { spring } from "@/lib/motion";
import type { NavItem as Item } from "@/lib/content/types";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { Badge } from "@/components/ui/primitives";
import { navGroups } from "@/lib/content/nav";
import { isLocalCollectionHref } from "@/lib/player-collections";
import { useContent } from "./ContentContext";
import { usePlayer } from "./PlayerContext";
import { VERTICALS } from "./SideNav";

/* The Tile template's alternative to the left rail: the destinations that
   live in SideNav's groups surface as two horizontal rows under the top bar
   instead — a product tab strip, then the category/quick-filter pills. Mobile
   stays on the shared tab bar and drawer in both templates, so this only
   ever renders at `lg:` and up. */
export function TileNav({ casinoNav }: { casinoNav: Item[] }) {
  const pathname = usePathname();
  const content = useContent();
  const player = usePlayer();
  const vertical = pathname.startsWith(SPORTSBOOK_ROOT) ? "sports" : "casino";

  const groups = navGroups(content.nav, casinoNav);
  /* A group that lists the casino categories is the pill row's. Every other
     group is one product tab, named for the group and pointing where its
     first link does — the first beside the verticals, the rest on the right
     ahead of the tail links, as the kit seats Promotions and Leaderboards. */
  const [lead, ...trailing] = groups
    .filter((group) => !group.categories && group.items.length > 0)
    .map((group) => ({ ...group.items[0], label: group.label, icon: group.icon }));
  const primary = content.nav.primary.filter((item) => player.enabled || !isLocalCollectionHref(item.href));
  /* The quick links and the category list are two separate arrays, but the
     API's own category set can repeat a quick link by destination (its own
     "New Releases" lands on the same /c/new-releases as the primary one) —
     harmless as two different SideNav groups, a visible dupe as one row. */
  const primaryHrefs = new Set(primary.map((item) => item.href));
  const pills = [
    ...primary,
    ...groups.filter((group) => group.categories).flatMap((group) => group.items).filter((item) => !primaryHrefs.has(item.href)),
  ];

  /* Centered (NitroBet's layout): both halves of the tab row meet in the
     middle, and the pill row centres too — "safe" centring, so a row wider
     than the screen still starts at its first pill instead of clipping it. */
  const centered = isCenteredNav(content.flags.navLayout);

  if (isSingleRowNav(content.flags.navLayout)) {
    return (
      <div className="sticky top-20 z-30 hidden bg-elevation-1 lg:block">
        <nav aria-label="Menu" className="scrollbar-none flex items-center gap-[8px] overflow-x-auto overflow-y-hidden px-[16px] py-[12px]">
          {[...primary, ...content.nav.tail].map((item) => (
            <CategoryPill key={`${item.href}-${item.label}`} item={item} active={pathname === item.href} />
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div className="sticky top-20 z-30 hidden flex-col bg-elevation-1 lg:flex">
      <nav
        aria-label="Product"
        className={cn(
          "flex h-[59px] w-full items-center border-b border-border px-[16px]",
          centered && "justify-center",
        )}
      >
        <div className={cn("flex h-full items-center", !centered && "flex-1")}>
          {content.flags.sportsEnabled ? (
            VERTICALS.map((v) => (
              <ProductTab
                key={v.key}
                item={v}
                active={vertical === v.key}
              />
            ))
          ) : (
            <span className="flex h-full items-center gap-[4px] px-[16px] text-[14px] font-bold leading-[1.6] text-fg">
              <Icon name="spade" size={18} />
              Casino
            </span>
          )}
          {lead && <ProductTab item={lead} active={pathname === lead.href} />}
        </div>
        <div className={cn("flex h-full items-center", !centered && "justify-end")}>
          {trailing.map((tab) => (
            <ProductTab key={tab.label} item={tab} active={pathname === tab.href} />
          ))}
          {content.nav.tail.map((item) => (
            <ProductTab key={item.label} item={item} active={pathname === item.href} />
          ))}
        </div>
      </nav>

      <nav
        aria-label="Categories"
        className={cn(
          "scrollbar-none flex items-center gap-[8px] overflow-x-auto overflow-y-hidden p-[16px]",
          centered && "justify-center-safe",
        )}
      >
        {pills.map((item) => (
          <CategoryPill key={item.href} item={item} active={pathname === item.href} />
        ))}
      </nav>
    </div>
  );
}

function ProductTab({ item, active }: { item: Item; active: boolean }) {
  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex h-full shrink-0 items-center gap-[4px] px-[16px] text-[14px] font-bold leading-[1.6] transition-colors duration-200",
        active ? "text-fg" : "text-fg-subtle hover:text-fg",
      )}
    >
      <Icon name={item.icon} size={18} />
      {item.label}
      {item.badge && <Badge>{item.badge}</Badge>}
      {active && (
        <motion.span
          layoutId="tile-product-tab"
          transition={spring}
          className="absolute inset-x-[16px] bottom-0 h-[2px] rounded-full bg-brand"
        />
      )}
    </Link>
  );
}

function CategoryPill({ item, active }: { item: Item; active: boolean }) {
  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex h-[40px] shrink-0 items-center gap-[8px] rounded-rounded px-[20px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
        active ? "text-fg-on-primary" : "bg-elevation-2 text-fg-subtle hover:text-fg",
      )}
    >
      {active && (
        <motion.span
          layoutId="tile-category-pill"
          transition={spring}
          className="absolute inset-0 rounded-rounded bg-brand"
        />
      )}
      {item.iconUrl ? (
        <span className="relative grid h-[18px] w-[18px] shrink-0 place-items-center overflow-hidden rounded-[5px] bg-elevation-3">
          <RemoteImage src={item.iconUrl} alt="" width={18} height={18} />
        </span>
      ) : (
        <Icon name={item.icon} size={18} className="relative" />
      )}
      <span className="relative">{item.label}</span>
      {item.badge && <Badge className="relative">{item.badge}</Badge>}
    </Link>
  );
}
